import { useLatest } from '@cloudio-saas/statex';
import {
  Dispatch,
  SetStateAction,
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react';

export default function useLocalState<T>(
  value: T,
  onChange: (val: T) => void,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(value);
  const valueRef = useRef<T>(value);
  const ref = useRef<T[]>([value]);
  const onChangeRef = useLatest(onChange);
  const defState = useDeferredValue(state);

  useEffect(() => {
    if (value === valueRef.current) {
      // console.log(
      //   'initial value or same as the latest value posted to onChange callback',
      // );
      ref.current = [];
      return;
    }
    // ignore if the value is part of any of the previously posted value
    // this may happen if the local state changes much faster pace than
    // the store value
    if (!ref.current.includes(value)) {
      // console.log(ref.current, value);
      // console.log('value got changed outside... hence update the local state');
      valueRef.current = value;
      ref.current = [];
      setState(value);
    }
  }, [value]);

  useEffect(() => {
    // avoid calling for initial value
    if (valueRef.current !== defState) {
      startTransition(() => {
        valueRef.current = defState;
        // keep a track on all values being posted to onChange callback
        ref.current.push(defState);
        onChangeRef.current(defState);
      });
    }
  }, [onChangeRef, defState]);

  const setValue = useCallback((val: SetStateAction<T>) => {
    setState((v) => {
      let _val: T;
      if (typeof val === 'function') {
        // @ts-ignore val is fn
        _val = val(v);
      } else {
        _val = val;
      }
      return _val;
    });
  }, []);
  return [state, setValue];
}
